import { Button } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { BaseDialog } from '../../../../base/components/BaseDialog';
import { Flex } from '../../../../base/components/Flex';
import FormikForm from '../../../../base/components/form/FormikForm';
import FormikTextField from '../../../../base/components/form/FormikTextField';
import { EditCompanyQrSceneDocument } from '../../../../graphql/graphql-operations';
import { useMutationWithSnackbar } from '../../../../hooks/useMutationWithSnackbar';
import {
  TFunctionPrefixed,
  useTranslationPrefix,
} from '../../../../hooks/useTranslationPrefix';
import { CompanyQrScene } from '../CompanyQRScenesPage';

interface FormData {
  additionalParams: string;
  launchTimoutSeconds: string;
}

interface Props {
  scene: CompanyQrScene;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditCompanyQrSceneDialog = ({ scene, onClose, onSuccess }: Props) => {
  const { _t } = useTranslationPrefix('EditCompanyQrSceneDialog');

  const [editMutation] = useMutationWithSnackbar(
    'EditCompanyQrSceneDialog.editMutation',
    EditCompanyQrSceneDocument,
    {
      onCompleted: () => {
        onSuccess?.();
      },
    },
  );

  const handleSubmit = async (
    values: FormData,
    helpers: FormikHelpers<FormData>,
  ) => {
    await editMutation({
      variables: {
        input: {
          id: scene.id,
          launchTimoutSeconds: parseInt(values.launchTimoutSeconds),
          additionalParams: values.additionalParams,
        },
      },
    });
  };

  return (
    <BaseDialog
      open={!!scene}
      onClose={onClose}
      title={_t('title', 'Edit "{{name}}"', {
        name: scene.scene.name_t,
      })}
      disableEnforceFocus
    >
      <>
        <Formik<FormData>
          initialValues={{
            additionalParams: scene.additionalParams ?? '{}',
            launchTimoutSeconds: `${scene.launchTimoutSeconds ?? '0'}`,
          }}
          onSubmit={handleSubmit}
          enableReinitialize
          validationSchema={getValidationSchema(_t)}
        >
          {({ isSubmitting }) => (
            <FormikForm>
              <Flex flexDirection="column" gap={3}>
                <FormikTextField
                  fullWidth
                  name="launchTimoutSeconds"
                  label={_t('launchTimoutSeconds', 'Launch delay')}
                  InputProps={{
                    endAdornment: 's',
                  }}
                />
                <FormikTextField
                  fullWidth
                  name="additionalParams"
                  label={_t(
                    'dialog.additionalParams',
                    'Additional params (json)',
                  )}
                  multiline
                  minRows={3}
                  maxRows={8}
                />

                <Flex
                  justifyContent="center"
                  mt={1}
                  gap={3}
                  flexDirection={{ xs: 'column-reverse', md: 'row' }}
                >
                  <Button
                    id="create-playlist-folder-cancel"
                    onClick={onClose}
                    color="primary"
                    variant="outlined"
                    disabled={isSubmitting}
                  >
                    {_t('dialog.cancel', 'Cancel')}
                  </Button>
                  <Button
                    id="create-playlist-folder-confirm"
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {_t('dialog.submit', 'Save')}
                  </Button>
                </Flex>
              </Flex>
            </FormikForm>
          )}
        </Formik>
      </>
    </BaseDialog>
  );
};

export default EditCompanyQrSceneDialog;

const getValidationSchema = (_t: TFunctionPrefixed) =>
  Yup.object({
    additionalParams: Yup.string()
      .required(
        _t('additionalParamsRequired', 'Additional params are required'),
      )
      .test('json', _t('additionalParamsInvalid', 'Invalid json'), (value) => {
        try {
          JSON.parse(value || '');
          return true;
        } catch (error) {
          return false;
        }
      }),
    launchTimoutSeconds: Yup.number()
      .typeError(_t('launchTimoutSecondsRequired', 'Launch delay is required'))
      .required(_t('launchTimoutSecondsRequired', 'Launch delay is required'))
      .min(0, _t('launchTimoutSecondsMin', 'Launch delay cannot be negative'))
      .max(100, _t('launchTimoutSecondsRequired', 'Launch delay is too high')),
  });
